import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, useActionDialog } from '../../../components/ui';
import type { CycleRef } from '../../../api/plan-options';
import type { JawType, MeshAsset, TreatmentStage } from '../../../types/admin';
import {
  useDeleteMovementStepMesh,
  useReprocessMesh,
  useUploadMovementStepMesh,
} from '../hooks/usePlanOptions';
import { MeshUploadSlot } from './plan-options/MeshUploadSlot';
import { MeshViewerModalLazy } from './plan-options/MeshViewerModalLazy';
import styles from './plan-options/PlanOptions.module.scss';

const JAWS: readonly JawType[] = ['upper', 'lower'];

function slotKey(stepIndex: number, jaw: JawType): string {
  return `${stepIndex}:${jaw}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unexpected error';
}

export type MovementStepsPanelProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
  /**
   * Step rows the admin added that hold no mesh yet. Owned by the parent so the
   * panel can be mounted on more than one tab without losing them on switch.
   */
  readonly draftSteps: readonly number[];
  readonly onDraftStepsChange: (steps: readonly number[]) => void;
};

/**
 * Ordered tooth-movement sequence (step 0..N x upper/lower) shown to the patient
 * during payment and delivery.
 *
 * A step exists server-side only once it holds a mesh, so freshly added rows
 * live in `draftSteps` until the first upload lands.
 */
export function MovementStepsPanel({
  patientId,
  treatmentId,
  stage,
  draftSteps,
  onDraftStepsChange,
}: MovementStepsPanelProps) {
  const { t } = useTranslation();
  const { confirmAction } = useActionDialog();

  const cycle = useMemo<CycleRef>(
    () => ({ patientId, treatmentId, cycleId: stage.id }),
    [patientId, treatmentId, stage.id],
  );
  const uploadMutation = useUploadMovementStepMesh(cycle);
  const deleteMutation = useDeleteMovementStepMesh(cycle);
  const reprocessMutation = useReprocessMesh(cycle);

  const [isViewerOpen, setViewerOpen] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [busySlots, setBusySlots] = useState<ReadonlySet<string>>(() => new Set());
  const [deletingSteps, setDeletingSteps] = useState<ReadonlySet<number>>(() => new Set());

  const serverSteps = useMemo(() => stage.movement_steps ?? [], [stage.movement_steps]);

  const meshByCell = useMemo(() => {
    const map = new Map<string, MeshAsset>();
    serverSteps.forEach((step) => {
      (step.meshes ?? []).forEach((mesh) => map.set(slotKey(step.step_index, mesh.jaw), mesh));
    });
    return map;
  }, [serverSteps]);

  const visibleSteps = useMemo(() => {
    const indices = new Set<number>(serverSteps.map((step) => step.step_index));
    draftSteps.forEach((index) => indices.add(index));
    return [...indices].sort((a, b) => a - b);
  }, [serverSteps, draftSteps]);

  const allMeshes = useMemo(
    () => serverSteps.flatMap((step) => [...(step.meshes ?? [])]),
    [serverSteps],
  );

  const mutationCallbacks = {
    onError: (error: unknown) => setLastError(errorMessage(error)),
    onSuccess: () => setLastError(null),
  };

  function markSlot(key: string, busy: boolean) {
    setBusySlots((previous) => {
      const next = new Set(previous);
      if (busy) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  function runSlotMutation(key: string, run: (settle: () => void) => void) {
    setLastError(null);
    markSlot(key, true);
    run(() => markSlot(key, false));
  }

  function handleAddStep() {
    const next = visibleSteps.length === 0 ? 0 : Math.max(...visibleSteps) + 1;
    onDraftStepsChange([...draftSteps, next]);
  }

  async function handleDeleteStep(stepIndex: number) {
    const existing = JAWS.filter((jaw) => meshByCell.has(slotKey(stepIndex, jaw)));

    if (existing.length === 0) {
      onDraftStepsChange(draftSteps.filter((index) => index !== stepIndex));
      return;
    }

    const confirmed = await confirmAction({
      message: t('movementSteps.confirmDeleteStep', { index: stepIndex }),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    });
    if (!confirmed) return;

    setLastError(null);
    setDeletingSteps((previous) => new Set(previous).add(stepIndex));

    // Both jaws are attempted even if one fails, and a partial failure is
    // reported rather than swallowed — dropping the row then would hide a mesh
    // that is still on the server.
    const outcomes = await Promise.allSettled(
      existing.map((jaw) => deleteMutation.mutateAsync({ stepIndex, jaw })),
    );

    setDeletingSteps((previous) => {
      const next = new Set(previous);
      next.delete(stepIndex);
      return next;
    });

    const failures = outcomes.filter(
      (outcome): outcome is PromiseRejectedResult => outcome.status === 'rejected',
    );

    if (failures.length > 0) {
      setLastError(failures.map((failure) => errorMessage(failure.reason)).join('\n'));
      return;
    }
    onDraftStepsChange(draftSteps.filter((index) => index !== stepIndex));
  }

  return (
    <div className={styles.section} data-testid="movement-steps-panel">
      <header className={styles.sectionHeader}>
        <div className={styles.sectionHeaderText}>
          <h4 className={styles.sectionTitle}>{t('movementSteps.title')}</h4>
          <p className={styles.sectionSubtitle}>{t('movementSteps.subtitle')}</p>
        </div>
        <div className={styles.cardActions}>
          <Button
            variant="secondary"
            size="sm"
            disabled={allMeshes.length === 0}
            onClick={() => setViewerOpen(true)}
          >
            {t('planOptions.view3d')}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleAddStep}>
            {t('movementSteps.addStep')}
          </Button>
        </div>
      </header>

      {lastError && <p className={styles.inlineError}>{lastError}</p>}

      {visibleSteps.length === 0 ? (
        <p className={styles.emptyState}>{t('movementSteps.empty')}</p>
      ) : (
        <div className={styles.stepList}>
          {visibleSteps.map((stepIndex) => (
            <div key={stepIndex} className={styles.stepRow}>
              <div className={styles.stepRowHeader}>
                <h5 className={styles.stepRowTitle}>
                  {t('movementSteps.stepLabel', { index: stepIndex })}
                </h5>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={deletingSteps.has(stepIndex)}
                  onClick={() => void handleDeleteStep(stepIndex)}
                >
                  {t('movementSteps.deleteStep')}
                </Button>
              </div>
              <div className={styles.meshGrid}>
                {JAWS.map((jaw) => {
                  const key = slotKey(stepIndex, jaw);
                  const mesh = meshByCell.get(key) ?? null;
                  return (
                    <MeshUploadSlot
                      key={jaw}
                      label={t(`planOptions.jaw_${jaw}`)}
                      accessibleLabel={`${t('movementSteps.stepLabel', {
                        index: stepIndex,
                      })} · ${t(`planOptions.jaw_${jaw}`)}`}
                      mesh={mesh}
                      isBusy={busySlots.has(key) || deletingSteps.has(stepIndex)}
                      onSelectFile={(file) =>
                        runSlotMutation(key, (settle) =>
                          uploadMutation.mutate(
                            { stepIndex, jaw, file },
                            { ...mutationCallbacks, onSettled: settle },
                          ),
                        )
                      }
                      onDelete={() =>
                        runSlotMutation(key, (settle) =>
                          deleteMutation.mutate(
                            { stepIndex, jaw },
                            { ...mutationCallbacks, onSettled: settle },
                          ),
                        )
                      }
                      onReprocess={
                        mesh
                          ? () =>
                              runSlotMutation(key, (settle) =>
                                reprocessMutation.mutate(mesh.id, {
                                  ...mutationCallbacks,
                                  onSettled: settle,
                                }),
                              )
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {isViewerOpen && (
        <MeshViewerModalLazy
          open={isViewerOpen}
          onClose={() => setViewerOpen(false)}
          title={t('movementSteps.title')}
          cycle={cycle}
          meshes={allMeshes}
        />
      )}
    </div>
  );
}
